import React from 'react';
import { User } from '@supabase/supabase-js';

interface HeaderProps {
  systemStatus?: 'online' | 'offline' | 'maintenance';
  pollingActive?: boolean;
  user?: User | null;
  onSignOut?: () => Promise<void>;
}

const Header: React.FC<HeaderProps> = ({ systemStatus = 'online', pollingActive = false, user, onSignOut }) => {
  const getStatusColor = (status: string, polling: boolean) => {
    if (status === 'online' && polling) {
      return '#28a745'; // Green when online and polling
    }
    switch (status) {
      case 'online':
        return '#17a2b8'; // Blue when online but not polling
      case 'offline':
        return '#dc3545';
      case 'maintenance':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (status: string, polling: boolean) => {
    switch (status) {
      case 'online':
        return polling ? '🟢 Live Updates Active' : '🔵 Supabase Connected';
      case 'offline':
        return '🔴 Supabase Offline';
      case 'maintenance':
        return '🟡 Under Maintenance';
      default:
        return '⚪ Unknown Status';
    }
  };

  return (
    <header className="dashboard-header">
      <div className="header-content">
        <div className="title-section">
          <h1>HalalBooking Verification</h1>
          <p>AI-assisted hotel policy checks for halal-friendly stays</p>
        </div>

        <div className="header-right">
          <div className="user-section">
            {user && (
              <div className="user-info">
                <span className="user-email">{user.email}</span>
                <button onClick={onSignOut} className="sign-out-btn">
                  Sign Out
                </button>
              </div>
            )}
          </div>

          <div className="status-section">
            <div
              className="status-indicator"
              style={{ backgroundColor: getStatusColor(systemStatus, pollingActive) }}
            >
              <div className="status-dot"></div>
              <span>{getStatusText(systemStatus, pollingActive)}</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .dashboard-header {
          background: linear-gradient(135deg, var(--hb-sea) 0%, var(--hb-sea-dark) 60%, #18b6c7 100%);
          color: var(--hb-white);
          padding: 2.5rem;
          margin-bottom: 2.5rem;
          border-radius: var(--hb-radius-lg);
          box-shadow: var(--hb-shadow-lg);
          border: 1px solid rgba(255,255,255,0.2);
          position: relative;
          overflow: hidden;
        }

        .dashboard-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
          pointer-events: none;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .title-section h1 {
          margin: 0 0 0.75rem 0;
          font-size: 2.25rem;
          font-weight: 800;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .title-section p {
          margin: 0;
          opacity: 0.95;
          font-size: 1.1rem;
          font-weight: 500;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .status-section {
          display: flex;
          align-items: center;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.5rem;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          border: 1px solid rgba(255,255,255,0.2);
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: currentColor;
          box-shadow: 0 0 8px currentColor;
        }

        .user-section {
          display: flex;
          align-items: center;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: rgba(255, 255, 255, 0.15);
          padding: 0.875rem 1.25rem;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.2);
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .user-email {
          font-size: 0.9rem;
          font-weight: 500;
          opacity: 0.95;
        }

        .sign-out-btn {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          padding: 0.5rem 1rem;
          border-radius: var(--hb-radius);
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          backdrop-filter: blur(10px);
        }

        .sign-out-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          border-color: rgba(255, 255, 255, 0.5);
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .dashboard-header {
            padding: 2rem 1.5rem;
          }

          .header-content {
            flex-direction: column;
            gap: 1.5rem;
            text-align: center;
          }

          .title-section h1 {
            font-size: 2rem;
          }

          .header-right {
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;
